import { Directive, Input, Output, EventEmitter, OnInit, OnDestroy, ElementRef } from '@angular/core';

/**
 * ARCHITECTURE NOTE: Why this uses native addEventListener instead of @HostListener
 *
 * Problem: AG Grid cell editors call event.stopPropagation() during the bubbling phase,
 * which prevents @HostListener and Angular's event system from capturing Ctrl+C/X/V.
 *
 * Solution: Use native addEventListener with capture phase (true parameter).
 * The capture phase runs DOWN the DOM tree BEFORE the bubbling phase, executing
 * BEFORE AG Grid's stopPropagation() can block the event.
 */
@Directive({
  selector: '[miqTagClipboardKeyboard]',
  standalone: true,
})
export class TagClipboardKeyboardDirective implements OnInit, OnDestroy {
  @Input() selectedTagIds: Set<number> = new Set();
  @Input() viewTags: any[] = [];
  @Input() enableClipboardFeatures = false;
  @Input() canEdit = false;
  @Input() entityId: number | string;
  @Output() clipboardCopy = new EventEmitter<{ tagNames: string[] }>();
  @Output() clipboardCut = new EventEmitter<{ tagNames: string[] }>();
  @Output() clipboardPaste = new EventEmitter<{ clipboardTags: string[] }>();

  private keydownHandler = (event: KeyboardEvent) => this.handleKeyboardEvent(event);

  constructor(private el: ElementRef) {}

  ngOnInit(): void {
    // Attach native event listener with capture phase (true parameter)
    // This bypasses Angular's event system and AG Grid's event suppression
    // The capture phase runs BEFORE AG Grid can call stopPropagation()
    this.el.nativeElement.addEventListener('keydown', this.keydownHandler, true);
  }

  ngOnDestroy(): void {
    this.el.nativeElement.removeEventListener('keydown', this.keydownHandler, true);
  }

  private handleKeyboardEvent(event: KeyboardEvent): void {
    if (!this.canEdit || !this.enableClipboardFeatures) {
      return;
    }
    const isCtrlOrCmd = event.ctrlKey || event.metaKey;

    // Copy selected tags
    if (isCtrlOrCmd && event.key.toLowerCase() === 'c' && this.selectedTagIds.size > 0) {
      event.preventDefault();
      this.copySelectedTags();
    }

    // Cut selected tags
    if (isCtrlOrCmd && event.key.toLowerCase() === 'x' && this.selectedTagIds.size > 0) {
      event.preventDefault();
      this.cutSelectedTags();
    }

    // Paste tags
    if (isCtrlOrCmd && event.key.toLowerCase() === 'v') {
      event.preventDefault();
      this.pasteClipboardTags();
    }
  }

  private copySelectedTags(): void {
    const selectedTags = this.viewTags.filter(tag => this.selectedTagIds.has(tag.internalId)).map(tag => tag.display);

    if (selectedTags.length === 0) return;

    this.writeToSystemClipboard(selectedTags);
    this.clipboardCopy.emit({ tagNames: selectedTags });
  }

  private cutSelectedTags(): void {
    const selectedTags = this.viewTags.filter(tag => this.selectedTagIds.has(tag.internalId)).map(tag => tag.display);

    if (selectedTags.length === 0) return;

    this.writeToSystemClipboard(selectedTags);
    this.clipboardCut.emit({ tagNames: selectedTags });
  }

  private pasteClipboardTags(): void {
    // Read from system clipboard and emit
    navigator.clipboard.readText().then(clipboardText => {
      const tagNames = this.parseClipboardTags(clipboardText);

      // Filter out tags that already exist in viewTags by name
      const existingTagNames = new Set(this.viewTags.map(tag => tag.name));
      const newTags = tagNames.filter(tagName => !existingTagNames.has(tagName));

      if (newTags.length > 0) {
        this.clipboardPaste.emit({ clipboardTags: newTags });
      }
    });
  }

  private parseClipboardTags(csvText: string): string[] {
    if (!csvText.trim()) return [];

    // Parse quoted CSV format: "tag1","tag2","tag3"
    const quotedRegex = /"([^"]*)"/g;
    const tags: string[] = [];
    let match;

    while ((match = quotedRegex.exec(csvText)) !== null) {
      tags.push(match[1]);
    }

    return tags;
  }

  private writeToSystemClipboard(tagNames: string[]): void {
    const csvText = tagNames.map(tag => `"${tag}"`).join(',');
    navigator.clipboard.writeText(csvText);
  }
}
